import { CommonModule, DatePipe } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import {
  GetListWoundByPatientIdResponse,
  WoundCategoryType,
  WoundStatus,
} from 'app/core/models/wound/wound.model';
import { WoundService } from 'app/core/services/wound/wound.service';
import { ToastService } from 'app/core/services/toast.service';

export interface WoundGroup {
  parent: GetListWoundByPatientIdResponse;
  children: GetListWoundByPatientIdResponse[];
}

@Component({
  selector: 'app-wound-timeline',
  standalone: true,
  imports: [CommonModule, TranslateModule, RouterLink, DatePipe],
  templateUrl: './wound-timeline.component.html',
  styleUrl: './wound-timeline.component.scss',
})
export class WoundTimelineComponent implements OnChanges {
  @Input() wounds: GetListWoundByPatientIdResponse[] = [];
  @Input() patientId: string = '';
  @Input() sectionTitle: string = 'patientDetail.wounds';
  @Input() emptyStateText: string = 'noWounds';
  @Input() showProgressBar: boolean = true;
  @Input() navigateOnClick: boolean = true;
  @Output() woundClicked = new EventEmitter<string>();
  @Output() woundArchived = new EventEmitter<string>();
  @Output() woundUnarchived = new EventEmitter<string>();
  @Output() allWoundIdsCalculated = new EventEmitter<string[]>();

  groupedWounds: WoundGroup[] = [];
  archivingWoundId: string | null = null;

  // Tooltip properties
  activeTooltip: { text: string; x: number; y: number } | null = null;
  private tooltipTimeout: any = null;
  private pendingTooltipPosition: { x: number; y: number } | null = null;

  constructor(
    private woundService: WoundService,
    private toastService: ToastService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['wounds']) {
      this.updateGroupedWounds();
    }
  }

  private updateGroupedWounds(): void {
    if (!this.wounds || this.wounds.length === 0) {
      this.groupedWounds = [];
      return;
    }

    const parents = this.wounds.filter(
      (w) => w.woundCategoryType === WoundCategoryType.Initial,
    );

    const grouped = parents.map((parent) => {
      const children: GetListWoundByPatientIdResponse[] = (
        parent.followUps || []
      )
        .map(
          (followUp) =>
            ({
              id: followUp.id,
              createdDateTime: followUp.createdDateTime,
              woundStatus: followUp.woundStatus,
              woundCategoryType: followUp.woundCategoryType,
              woundType: followUp.woundType,
              isActive: followUp.isActive,
              parentWoundId: parent.id,
              followUps: [],
            }) as GetListWoundByPatientIdResponse,
        )
        .sort(
          (a, b) =>
            new Date(a.createdDateTime).getTime() -
            new Date(b.createdDateTime).getTime(),
        );

      return { parent, children };
    });

    grouped.sort((a, b) => {
      const aIsArchived = this.isGroupArchivedInternal(a);
      const bIsArchived = this.isGroupArchivedInternal(b);

      if (aIsArchived !== bIsArchived) {
        return aIsArchived ? 1 : -1;
      }
      const aLatestDate =
        a.children.length > 0
          ? new Date(a.children[a.children.length - 1].createdDateTime)
          : new Date(a.parent.createdDateTime);
      const bLatestDate =
        b.children.length > 0
          ? new Date(b.children[b.children.length - 1].createdDateTime)
          : new Date(b.parent.createdDateTime);

      return bLatestDate.getTime() - aLatestDate.getTime();
    });

    this.groupedWounds = grouped;

    this.allIndividualWoundIds();
  }

  private isGroupArchivedInternal(group: WoundGroup): boolean {
    const lastItem =
      group.children.length > 0
        ? group.children[group.children.length - 1]
        : group.parent;
    return !lastItem.isActive;
  }

  isGroupArchived(group: WoundGroup): boolean {
    return this.isGroupArchivedInternal(group);
  }

  getStatusClass(status: number, isActive: boolean = true): string {
    if (!isActive) {
      return 'status-inactive';
    }

    const statusClasses: Record<number, string> = {
      0: 'status-active',
      1: 'status-active',
      2: 'status-past',
      3: 'status-current',
      4: 'status-past',
    };
    return statusClasses[status] || 'status-inactive';
  }

  getWoundStatusKey(status: number): string {
    const keys: Record<number, string> = {
      0: 'patientDetail.woundStatus.created',
      1: 'patientDetail.woundStatus.waitingDoctorAssignment',
      2: 'patientDetail.woundStatus.doctorAssigned',
      3: 'patientDetail.woundStatus.treatmentDecisionCreated',
      4: 'patientDetail.woundStatus.treatmentCompleted',
    };
    return keys[status] || 'patientDetail.unknown';
  }

  canArchive(group: WoundGroup): boolean {
    const lastItem =
      group.children.length > 0
        ? group.children[group.children.length - 1]
        : group.parent;
    return (
      lastItem.woundStatus === WoundStatus.TreatmentCompleted &&
      lastItem.isActive
    );
  }

  getArchiveWoundId(group: WoundGroup): string {
    return group.children.length > 0
      ? group.children[group.children.length - 1].id
      : group.parent.id;
  }

  onArchive(group: WoundGroup): void {
    const woundId = this.getArchiveWoundId(group);
    this.archivingWoundId = woundId;

    this.woundService.archiveWound(woundId).subscribe({
      next: () => {
        this.toastService.showToast('Wound archived successfully', 'success');
        this.archivingWoundId = null;
        this.woundArchived.emit(woundId);
      },
      error: () => {
        this.toastService.showToast('Failed to archive wound', 'error');
        this.archivingWoundId = null;
      },
    });
  }

  onUnarchive(group: WoundGroup): void {
    const woundId = this.getArchiveWoundId(group);
    this.archivingWoundId = woundId;

    this.woundService.unarchiveWound(woundId).subscribe({
      next: () => {
        this.toastService.showToast('Wound unarchived successfully', 'success');
        this.archivingWoundId = null;
        this.woundUnarchived.emit(woundId);
      },
      error: () => {
        this.toastService.showToast('Failed to unarchive wound', 'error');
        this.archivingWoundId = null;
      },
    });
  }

  allIndividualWoundIds() {
    const ids: string[] = this.groupedWounds.reduce((acc, group) => {
      acc.push(group.parent.id);

      if (group.children && group.children.length > 0) {
        const childIds = group.children.map((child) => child.id);
        acc.push(...childIds);
      }

      return acc;
    }, [] as string[]);

    this.allWoundIdsCalculated.emit(ids);
  }

  handleWoundClick(event: Event, woundId: string): void {
    if (!this.navigateOnClick) {
      event.preventDefault();
      event.stopPropagation();
      this.woundClicked.emit(woundId);
    }
  }

  showTooltip(event: MouseEvent, tooltip: string) {
    if (!tooltip) return;

    this.pendingTooltipPosition = {
      x: event.clientX + 12,
      y: event.clientY - 12,
    };

    if (this.tooltipTimeout) {
      clearTimeout(this.tooltipTimeout);
    }

    this.tooltipTimeout = setTimeout(() => {
      if (this.pendingTooltipPosition) {
        this.activeTooltip = {
          text: tooltip,
          x: this.pendingTooltipPosition.x,
          y: this.pendingTooltipPosition.y,
        };
      }
    }, 300);
  }

  updateTooltipPosition(event: MouseEvent) {
    const newX = event.clientX + 12;
    const newY = event.clientY - 12;

    if (this.pendingTooltipPosition) {
      this.pendingTooltipPosition.x = newX;
      this.pendingTooltipPosition.y = newY;
    }

    if (this.activeTooltip) {
      this.activeTooltip.x = newX;
      this.activeTooltip.y = newY;
    }
  }

  hideTooltip() {
    if (this.tooltipTimeout) {
      clearTimeout(this.tooltipTimeout);
      this.tooltipTimeout = null;
    }
    this.pendingTooltipPosition = null;
    this.activeTooltip = null;
  }

  getDoctorName(wound: GetListWoundByPatientIdResponse): string {
    return wound.doctorName || 'Doctor';
  }
}
